import { useCallback, useEffect, useState } from 'react';
import { getLanguage, type Language } from './i18n';

export type ScheduleNameKind = 'club' | 'team' | 'league';

export type ScheduleNameEntry = {
  id: string;
  kind: ScheduleNameKind;
  zh: string;
  en: string;
  ru: string;
  aliases: string[];
};

type ScheduleNameDictionary = {
  version: string;
  source: string;
  entries: ScheduleNameEntry[];
};

type LoadedScheduleDictionary = {
  entries: ScheduleNameEntry[];
  index: Map<string, ScheduleNameEntry>;
};

let loadedDictionary: LoadedScheduleDictionary | null = null;
let dictionaryPromise: Promise<LoadedScheduleDictionary> | null = null;

const removableSuffixTokens = new Set(['fc', 'afc', 'cf', 'sc', 'ac', 'club', 'team']);

export function normalizeScheduleName(value: string): string {
  return value.normalize('NFKC').trim().toLowerCase();
}

export function canonicalizeScheduleName(value: string): string {
  let canonical = normalizeScheduleName(value)
    .replace(/[·•.,'’`()[\]{}]/g, ' ')
    .replace(/\b(?:f\s*c|a\s*f\s*c|c\s*f|s\s*c|a\s*c)\b\.?/gi, ' ')
    .replace(/\b(?:football club|association football club)\b\.?/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  canonical = canonical.replace(/(?:足球俱乐部|足球會|足球会|俱乐部|球会|足球队|足球隊)$/u, '').trim();
  const tokens = canonical.split(' ').filter(Boolean);
  while (tokens.length > 1 && removableSuffixTokens.has(tokens[0]!)) tokens.shift();
  while (tokens.length > 1 && removableSuffixTokens.has(tokens[tokens.length - 1]!)) tokens.pop();
  return tokens.join(' ').trim();
}

function setScheduleNameIndex(index: Map<string, ScheduleNameEntry>, key: string, entry: ScheduleNameEntry): void {
  if (!key) return;
  const existing = index.get(key);
  const entryIsManual = entry.id.startsWith('manual-');
  const existingIsManual = existing?.id.startsWith('manual-') ?? false;
  if (!existing || (entryIsManual && !existingIsManual) || (!entryIsManual && !existingIsManual && existing.kind !== entry.kind)) {
    index.set(key, entry);
  }
}

export function buildScheduleNameIndex(entries: ScheduleNameEntry[]): Map<string, ScheduleNameEntry> {
  const index = new Map<string, ScheduleNameEntry>();
  for (const entry of entries) {
    for (const value of [entry.zh, entry.en, entry.ru, ...entry.aliases]) {
      const exactKey = normalizeScheduleName(value ?? '');
      setScheduleNameIndex(index, exactKey, entry);
      const canonicalKey = canonicalizeScheduleName(value ?? '');
      if (canonicalKey !== exactKey) setScheduleNameIndex(index, canonicalKey, entry);
    }
  }
  return index;
}

export function localizeScheduleNameWithIndex(
  value: string,
  language: Language,
  index: Map<string, ScheduleNameEntry> | null | undefined
): string {
  if (!value || !index) return value;
  const entry = index.get(normalizeScheduleName(value)) ?? index.get(canonicalizeScheduleName(value));
  if (!entry) return value;
  const selected = entry[language];
  return selected || entry.zh || value;
}

export async function loadScheduleNameDictionary(): Promise<LoadedScheduleDictionary> {
  if (loadedDictionary) return loadedDictionary;
  if (!dictionaryPromise) {
    dictionaryPromise = import('./schedule-name-dictionary.json')
      .then((module) => {
        const dictionary = ((module.default ?? module) as unknown) as ScheduleNameDictionary;
        const entries = Array.isArray(dictionary.entries) ? dictionary.entries : [];
        loadedDictionary = { entries, index: buildScheduleNameIndex(entries) };
        return loadedDictionary;
      })
      .catch(() => {
        loadedDictionary = { entries: [], index: new Map() };
        return loadedDictionary;
      });
  }
  return dictionaryPromise;
}

export function useScheduleNameLocalizer(): (value: string) => string {
  const [language, setLanguageState] = useState<Language>(getLanguage);
  const [dictionary, setDictionary] = useState<LoadedScheduleDictionary | null>(loadedDictionary);

  useEffect(() => {
    const syncLanguage = () => setLanguageState(getLanguage());
    window.addEventListener('ai-voice-language-change', syncLanguage);
    return () => window.removeEventListener('ai-voice-language-change', syncLanguage);
  }, []);

  useEffect(() => {
    if (dictionary) return;
    let active = true;
    void loadScheduleNameDictionary().then((loaded) => {
      if (active) setDictionary(loaded);
    });
    return () => { active = false; };
  }, [dictionary]);

  return useCallback(
    (value: string) => localizeScheduleNameWithIndex(value, language, dictionary?.index),
    [dictionary, language]
  );
}